import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData } from "@/data";
import { getTrackShareLinkQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { copyTextHandler, displayErrorHandler } from "@/utils";

const useGetTrackShareLink = (trackId, skip) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { getTrackShareLinkPending: getTrackShareLinkPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const { data, error, loading } = useQuery(getTrackShareLinkQuery, {
        skip: !isAuthenticated || !skip,
        variables: { id: trackId },
    });

    useEffect(() => {
        if (!data?.shareLinkTrack) return;

        toast.dismiss(`${getTrackShareLinkPendingToastName}_${toastId}`);

        copyTextHandler(
            import.meta.env.VITE_BACKEND_DOMAIN + data.shareLinkTrack,
            dispatch,
            t("messages.shareLinkMsgs.success", { entity: `${t("tracks.entity")} ${t("labels.shareLink")}` }),
        );
    }, [data]); // eslint-disable-line

    useEffect(() => {
        if (error) toast.dismiss(`${getTrackShareLinkPendingToastName}_${toastId}`);
    }, [error]); // eslint-disable-line

    const copyTrackShareLink = () => {
        copyTextHandler(
            import.meta.env.VITE_BACKEND_DOMAIN + data?.shareLinkTrack,
            dispatch,
            t("messages.copied", { entity: `${t("tracks.entity")} ${t("labels.shareLink")}` }),
        );
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.shareLinkMsgs.pending", { entity: t("tracks.entity") }), {
                    toastId: `${getTrackShareLinkPendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    useEffect(
        () => {
            if (error?.message && !loading) {
                displayErrorHandler(
                    error?.message === constantsData.failedToFetch ? t("messages.networkFailure") : error?.message,
                    dispatch,
                    openAlert,
                );
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        copyTrackShareLink,
        data: data?.shareLinkTrack,
    };
};

export default useGetTrackShareLink;
